import React, { useState } from 'react';
import { Package, Plus, Send, X } from 'lucide-react';
import { barrelService } from '../../../services/barrelService';

export default function ReplenishmentPanel({ lowStockAlerts, thresholds, onReplenishmentCreated }) {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState({
    barrelType: 'noir_270l',
    quantity: 0,
    supplierName: '',
    supplierContact: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const getReorderSuggestions = () => {
    const suggestions = [];
    
    lowStockAlerts?.forEach(alert => {
      if (alert?.severity === 'critical') {
        suggestions?.push({
          barrelType: alert?.barrelType,
          location: alert?.location,
          currentStock: alert?.currentStock,
          suggestedQuantity: thresholds?.[alert?.barrelType]?.reorder,
          priority: 'high'
        });
      }
    });
    
    return suggestions;
  };

  const handleCreateOrder = async () => {
    if (!orderForm?.supplierName || orderForm?.quantity <= 0) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }

    try {
      setSubmitting(true);
      
      const unitPrice = orderForm?.barrelType === 'noir_270l' ? 35 : 40;
      const totalPrice = orderForm?.quantity * unitPrice;

      await barrelService?.createBarrelOrder({
        customer_name: orderForm?.supplierName,
        customer_phone: orderForm?.supplierContact,
        customer_address: 'Fournisseur - Réapprovisionnement',
        customer_city: 'Fournisseur',
        barrel_type: orderForm?.barrelType,
        quantity: orderForm?.quantity,
        unit_price: unitPrice,
        total_price: totalPrice,
        status: 'pending',
        admin_notes: `Commande de réapprovisionnement - ${orderForm?.notes}`
      });

      alert('Commande de réapprovisionnement créée avec succès');
      setShowOrderModal(false);
      setOrderForm({
        barrelType: 'noir_270l',
        quantity: 0,
        supplierName: '',
        supplierContact: '',
        notes: ''
      });
      onReplenishmentCreated?.();
    } catch (error) {
      console.error('Error creating replenishment order:', error);
      alert('Erreur lors de la création de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickOrder = (suggestion) => {
    setOrderForm({
      barrelType: suggestion?.barrelType,
      quantity: suggestion?.suggestedQuantity,
      supplierName: '',
      supplierContact: '',
      notes: `Réapprovisionnement automatique pour ${suggestion?.location}`
    });
    setShowOrderModal(true);
  };

  const suggestions = getReorderSuggestions();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Réapprovisionnement Automatique</h2>
        </div>
        <button
          onClick={() => setShowOrderModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={18} />
          Nouvelle Commande
        </button>
      </div>
      {/* Reorder Suggestions */}
      {suggestions?.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700">Suggestions de Réapprovisionnement</h3>
          {suggestions?.map((suggestion, index) => (
            <div 
              key={index}
              className="bg-orange-50 border border-orange-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-orange-600 text-white text-xs font-semibold rounded-full">
                      PRIORITÉ HAUTE
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {suggestion?.barrelType === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">
                    Emplacement: <span className="font-medium">{suggestion?.location}</span>
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>Stock actuel: <span className="font-semibold text-red-600">{suggestion?.currentStock}</span></span>
                    <span>Quantité suggérée: <span className="font-semibold text-green-600">{suggestion?.suggestedQuantity}</span></span>
                  </div>
                </div>
                <button
                  onClick={() => handleQuickOrder(suggestion)}
                  className="ml-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Send size={16} />
                  Commander
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Package size={48} className="mx-auto mb-2 text-gray-300" />
          <p>Aucune suggestion de réapprovisionnement</p>
          <p className="text-sm">Les niveaux de stock sont adéquats</p>
        </div>
      )}
      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Nouvelle Commande Réapprovisionnement</h3>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de Barrique *
                  </label>
                  <select
                    value={orderForm?.barrelType}
                    onChange={(e) => setOrderForm({ ...orderForm, barrelType: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="noir_270l">Barrique Noire 270L (35€)</option>
                    <option value="bleu_220l">Barrique Bleue 220L (40€)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={orderForm?.quantity}
                    onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e?.target?.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Entrez la quantité"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du Fournisseur *
                  </label>
                  <input
                    type="text"
                    value={orderForm?.supplierName}
                    onChange={(e) => setOrderForm({ ...orderForm, supplierName: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Nom du fournisseur"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Fournisseur
                  </label>
                  <input
                    type="text"
                    value={orderForm?.supplierContact}
                    onChange={(e) => setOrderForm({ ...orderForm, supplierContact: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Téléphone ou email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={orderForm?.notes}
                    onChange={(e) => setOrderForm({ ...orderForm, notes: e?.target?.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Notes additionnelles..."
                  ></textarea>
                </div>

                {/* Total */}
                {orderForm?.quantity > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Prix Total:</span>
                      <span className="text-xl font-bold text-gray-900">
                        {(orderForm?.quantity * (orderForm?.barrelType === 'noir_270l' ? 35 : 40))?.toFixed(2)} €
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={submitting || !orderForm?.supplierName || orderForm?.quantity <= 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Création...' : 'Créer Commande'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}